@use "@styles/tokens";
@use "@styles/component-mixins" as component;

// Visual source of truth: docs/specs/2026-08-28-tag-figma-rework.md.
// Colors come only from registry tokens; heights, the 20px X button and the
// half-pixel numeric paddings are Figma geometry, not spacing tokens.

@layer base-components {
    .tag {
        // Override hatch for container components (spec "Override hatch"):
        // set --tag-bg / --tag-fg / --tag-border via className.
        --tag-bg: transparent;
        --tag-fg: currentColor;
        --tag-border: transparent;
        --tag-bg-hover: var(--tag-bg);
        --tag-border-hover: var(--tag-border);
        --tag-x-hover-bg: transparent;
        --tag-padding-x: var(--space-8);

        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-4);

        border: var(--border-width) solid var(--tag-border);
        color: var(--tag-fg);
        background-color: var(--tag-bg);

        // Figma tags are single-line; when a container constrains the tag,
        // the label truncates (.label ellipsis) rather than overflowing.
        white-space: nowrap;
        width: fit-content;
        max-width: 100%;

        // Icons follow the recipe fg like Button; opt out per icon with the
        // global .icon-color-revert class.
        @include component.recipe-icon-color;
    }

    // ——— color recipes (solid) ———
    .accent {
        --tag-bg: var(--bg-accent-subtle);
        --tag-fg: var(--fg-accent);
        --tag-bg-hover: var(--bg-accent-subtle-hover);
        --tag-x-hover-bg: var(--bg-accent-subtle-hover);
    }

    .primary {
        --tag-bg: var(--bg-neutral);
        --tag-fg: var(--fg-primary);
        --tag-bg-hover: var(--bg-neutral-hover-on-fill);
        --tag-x-hover-bg: var(--bg-neutral-hover-on-fill);
    }

    .neutral {
        --tag-bg: var(--bg-neutral);
        --tag-fg: var(--fg-secondary);
        --tag-bg-hover: var(--bg-neutral-hover-on-fill);
        --tag-x-hover-bg: var(--bg-neutral-hover-on-fill);
    }

    .error {
        --tag-bg: var(--bg-error-subtle);
        --tag-fg: var(--fg-error);
        --tag-bg-hover: var(--bg-error-subtle-hover);
        --tag-x-hover-bg: var(--bg-error-subtle-hover);
    }

    // ——— outline recipes: transparent body, border carries the color ———
    .outline {
        --tag-bg: transparent;
        --tag-bg-hover: transparent;

        &.accent {
            --tag-border: var(--border-accent);
            --tag-border-hover: var(--border-accent-hover);
            --tag-x-hover-bg: var(--bg-accent-subtle);
        }

        &.primary,
        &.neutral {
            --tag-border: var(--border-default);
            --tag-border-hover: var(--border-hover);
            --tag-x-hover-bg: var(--bg-neutral);
        }

        &.error {
            --tag-border: var(--border-error);
            --tag-border-hover: var(--border-error-hover);
            --tag-x-hover-bg: var(--bg-error-subtle);
        }
    }

    // ——— hover: interactive tags only; body reverts while the X is hovered ———
    .interactive {
        transition:
            background-color var(--duration-fast) ease,
            border-color var(--duration-fast) ease;

        // Pointer only when the root itself is clickable (onClick renders a
        // button); a removable-only tag keeps the arrow, the X has its own.
        &:is(button) {
            cursor: pointer;
        }

        &:hover:not(:has(.xButton:hover)),
        &[data-forced-state="hover"] {
            background-color: var(--tag-bg-hover);
            border-color: var(--tag-border-hover);
        }
    }

    // ——— sizes (heights/paddings/radii are Figma geometry) ———
    .sizeLg {
        @include tokens.text-s-14-medium;

        --tag-padding-x: var(--space-8);
        --tag-height: 28px;

        height: var(--tag-height);
        padding: 0 var(--tag-padding-x);
        border-radius: var(--radius-m);

        .slot > svg {
            width: 12px;
            height: 12px;
        }

        &:has(.xButton) {
            padding-right: var(--space-4);
        }
    }

    .sizeMd {
        @include tokens.text-xs-12-medium;

        --tag-padding-x: var(--space-8);
        --tag-height: 24px;

        height: var(--tag-height);
        padding: 0 var(--tag-padding-x);
        border-radius: var(--radius-m);

        .slot > svg {
            width: 12px;
            height: 12px;
        }

        &:has(.xButton) {
            padding-right: var(--space-2);
        }
    }

    .sizeSm {
        @include tokens.text-2xs-10-medium;

        --tag-padding-x: var(--space-6);
        --tag-height: 20px;

        height: var(--tag-height);
        padding: 0 var(--tag-padding-x);
        border-radius: var(--radius-s);

        .slot > svg {
            width: 10px;
            height: 10px;
        }

        &.numeric {
            // ds-deviation: Figma Number padX at 20px; no half-pixel spacing token.
            padding: 0 6.5px;
            min-width: 20px;
        }
    }

    .sizeXs {
        @include tokens.text-2xs-10-medium;

        --tag-height: 16px;

        height: var(--tag-height);

        // ds-deviation: Figma Number padX at 16px; no half-pixel spacing token.
        padding: 0 4.5px;
        border-radius: var(--radius-s);
    }

    .numeric {
        justify-content: center;
    }

    // Square-at-one-digit: min-width equals the size's height.
    .sizeLg.numeric {
        min-width: 28px;
    }

    .sizeMd.numeric {
        min-width: 24px;
    }

    .sizeXs.numeric {
        min-width: 16px;
    }

    .slot {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    // Squarish slot content (a nested chip, avatar, shortcut) opts into a
    // 4px tighter leading pad, matching Button's `data-slot-box` trim.
    .tag:has(.slot > [data-slot-box]) {
        padding-left: calc(var(--tag-padding-x) - var(--space-4));
    }

    // --- avatar content: the padding follows what the tag holds ---
    // Avatars and avatar groups mark themselves `data-avatar-box`. A tag that
    // leads with one (in the slot, or in the label without a slot) keeps it as
    // far from its left edge as from the top: (height - box) / 2 from the outer
    // edge, 2px for a 16px avatar in a 20px tag. With no text after it the
    // right side matches, so the box sits centred. Icons and text keep the
    // size's padding.
    .tag:is(
        :has(> .slot > [data-avatar-box]),
        :not(:has(> .slot)):has(> .label > [data-avatar-box])
    ) {
        --tag-box-size: 16px;
        --tag-box-inset: calc(
            (var(--tag-height) - var(--tag-box-size)) / 2 - var(--border-width)
        );

        padding-left: var(--tag-box-inset);

        &:has(> :is(.slot, .label) > [data-avatar-box][data-size="sm"]) {
            --tag-box-size: 20px;
        }

        &:has(> :is(.slot, .label) > [data-avatar-box][data-size="md"]) {
            --tag-box-size: 24px;
        }

        &:not(:has(> .label[data-has-text], > .xButton)) {
            gap: 0;
            padding-right: var(--tag-box-inset);
        }
    }

    // An inline-block label would sit a box on its text baseline, off centre.
    .label:has(> [data-avatar-box]) {
        display: inline-flex;
        align-items: center;
        gap: var(--space-4);
    }

    .label {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    // Clickable and removable: the label is its own button running from the
    // left edge to the X, so the X's hover zone starts after the label's padding.
    .tag:has(> .labelButton) {
        gap: 0;
        padding-left: 0;
    }

    .labelButton {
        display: inline-flex;
        align-items: center;
        align-self: stretch;
        gap: var(--space-4);
        min-width: 0;
        padding: 0 var(--space-4) 0 var(--tag-padding-x);
        border: none;
        border-radius: inherit;
        background: transparent;
        color: inherit;
        font: inherit;
        cursor: pointer;
    }

    .xButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        width: 20px;
        height: 20px;
        padding: 0;
        border: none;
        border-radius: var(--radius-s);
        background: transparent;
        color: inherit;
        cursor: pointer;
        transition: background-color var(--duration-fast) ease;

        &:hover {
            background-color: var(--tag-x-hover-bg);
        }
    }

    .tag[data-forced-state="hoverX"] .xButton {
        background-color: var(--tag-x-hover-bg);
    }
}
